// 배송완료 취소 칸 — 같은 사진으로 함께 완료된 형제를 고르고, 보호자 정정 문자(기본 켬)와 함께 cancelHouseholdDelivery를 부른다. 기사 배송 상세·사장 배송 관제에서 쓴다
import { useEffect, useState } from "react";
import { getDocs, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { orgQuery } from "@/lib/org";
import { functions } from "@/lib/firebase";
import { useAuth } from "@/store/auth";
import { plainError } from "@/lib/plainError";
import { plainNotifyReason } from "@/lib/notifyReason";

interface Row {
  id: string;
  studentName?: string;
  school?: string;
  driverUid?: string;
  driverName?: string;
  photoPath?: string;
}

interface Result { canceled: number; corrections: number; correctionFailed: number; correctionReason: string }

/** studentId만 주면 그 학생의 이번 달 기록을 찾고, delivery를 주면 그 기록에서 시작한다 */
export default function CancelDelivery({
  studentId, delivery, yearMonth, onCanceled,
}: { studentId?: string; delivery?: Row; yearMonth: string; onCanceled?: (message: string) => void }) {
  const { user, role } = useAuth();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [correct, setCorrect] = useState(true); // 대표 결정 Q5 — 정정 문자는 기본으로 보낸다
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRows(null);
    setMsg("");
    (async () => {
      try {
        let base: Row | undefined = delivery;
        if (!base && studentId) {
          const s = await getDocs(orgQuery("deliveries", where("yearMonth", "==", yearMonth), where("studentId", "==", studentId)));
          base = s.docs[0] ? { id: s.docs[0].id, ...(s.docs[0].data() as Omit<Row, "id">) } : undefined;
        }
        if (!base) { if (alive) setRows([]); return; }
        let list: Row[] = [base];
        if (base.photoPath) {
          // 같은 사진 한 장으로 함께 완료된 형제 — 엉뚱한 집을 찍었으면 대개 다 같이 취소해야 한다
          const s = await getDocs(orgQuery("deliveries", where("yearMonth", "==", yearMonth), where("photoPath", "==", base.photoPath)));
          list = s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Row, "id">) }));
          if (!list.some((r) => r.id === base!.id)) list.unshift(base);
        }
        if (!alive) return;
        setRows(list);
        setChecked(new Set(list.map((r) => r.id)));
      } catch (e) {
        if (alive) setMsg(`❌ 배송 기록을 불러오지 못했습니다 — ${plainError(e)}`);
      }
    })();
    return () => { alive = false; };
  }, [open, studentId, delivery?.id, yearMonth]);

  const mine = (r: Row) => role === "admin" || r.driverUid === user?.uid;
  const picked = (rows || []).filter((r) => checked.has(r.id));
  const names = picked.map((r) => r.studentName).filter(Boolean).join("·") || "학생";

  const run = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    setMsg("");
    try {
      const fn = httpsCallable<{ deliveryIds: string[]; sendCorrection: boolean }, Result>(functions, "cancelHouseholdDelivery");
      const r = (await fn({ deliveryIds: picked.map((x) => x.id), sendCorrection: correct })).data;
      const sms = !correct
        ? "정정 문자는 보내지 않았습니다."
        : r.corrections > 0
          ? `보호자에게 정정 문자 ${r.corrections}통을 보냈습니다.${r.correctionFailed ? ` (${r.correctionFailed}통 실패)` : ""}`
          : `정정 문자는 안 갔습니다 — ${plainNotifyReason(r.correctionReason)}.`;
      const done = `✅ ${r.canceled}명 배송완료를 취소했습니다. 다시 「미배송」입니다. ${sms}`;
      setMsg(done);
      setRows([]);
      onCanceled?.(done); // 이 칸이 사라지는 화면(배송 상세)도 결과 문구를 보여 줄 수 있게 넘긴다
    } catch (e) {
      setMsg(`❌ 취소하지 못했습니다 — ${plainError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="space-y-1">
        <button className="btn-secondary w-full min-h-[44px]" onClick={() => setOpen(true)}>↩️ 배송완료 취소</button>
        {msg && <div className="text-sm text-slate-700" role="status">{msg}</div>}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-3 space-y-2 text-sm">
      <div className="font-bold text-rose-800">배송완료를 취소할까요?</div>
      <p className="text-rose-700 break-keep">취소하면 그 집은 다시 「미배송」이 되고 기사 실적에서도 빠집니다. 보호자 사진 링크도 닫힙니다.</p>
      {rows === null && !msg && <div className="text-slate-500">불러오는 중…</div>}
      {rows?.length === 0 && !msg && <div className="text-slate-600">이번 달 배송 기록이 없습니다(이미 취소됐을 수 있습니다).</div>}
      {rows && rows.length > 0 && !rows.every(mine) && (
        <div className="text-slate-700">다른 기사님이 완료한 배송이라 사장님만 취소할 수 있습니다.</div>
      )}
      {rows && rows.length > 0 && rows.every(mine) && (
        <>
          {rows.length > 1 && <div className="text-xs text-slate-600">같은 사진으로 함께 완료된 학생입니다. 제대로 받은 집은 체크를 빼세요.</div>}
          {rows.map((r) => (
            <label key={r.id} className="flex items-center gap-2 py-1 min-h-[36px]">
              <input
                type="checkbox"
                className="accent-rose-500"
                checked={checked.has(r.id)}
                onChange={() => setChecked((prev) => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
              />
              <span>{r.school} {r.studentName}{role === "admin" && r.driverName ? <span className="text-xs text-slate-500"> · 기사 {r.driverName}</span> : null}</span>
            </label>
          ))}
          <label className="flex items-start gap-2 py-1">
            <input type="checkbox" className="accent-rose-500 mt-1" checked={correct} onChange={(e) => setCorrect(e.target.checked)} />
            <span>
              보호자에게 정정 문자 보내기
              <span className="block text-xs text-slate-600 break-keep">
                「[무상우유] {names} 배송완료 안내는 잘못 보냈습니다. 배송하면 다시 알려 드립니다.」 — 처음 배송완료 알림이 간 집에만 갑니다(건당 문자 요금).
              </span>
            </span>
          </label>
          <button className="btn-danger w-full min-h-[48px]" disabled={busy || picked.length === 0} onClick={run}>
            {busy ? "취소하는 중…" : `선택한 ${picked.length}명 배송완료 취소`}
          </button>
        </>
      )}
      {msg && <div className="text-slate-800" role="status">{msg}</div>}
      <button className="btn-ghost w-full min-h-[40px]" onClick={() => setOpen(false)}>닫기</button>
    </div>
  );
}
